import { sfAnd, sfEqual } from 'spring-filter-query-builder';
import { ListOfValuesReadDTO } from '../../api/dto';

/**
 * Spring filter-query-builder clause shape.
 * We only rely on `toString()` because API requests need the filter as a string.
 */
type SpringFilterClause = {
    toString(): string;
};

/**
 * Shape of one row in SearchPage filter state.
 * Example: { field: 'document.facility.code', operator: 'IS', value: { code: 'PS' } }
 */
type WatchedFilter = {
    field: string;
    value: unknown;
};

/**
 * Minimal domain shape we care about when extracting a code from UI values.
 */
type ListOfValuesLike = Pick<ListOfValuesReadDTO, 'code'>;

/**
 * Generic runtime guard for unknown values coming from form watches.
 */
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * Runtime guard for values that behave like ListOfValuesReadDTO.
 */
const isListOfValuesLike = (value: unknown): value is ListOfValuesLike =>
    isRecord(value) && typeof value.code === 'string';

/**
 * Runtime guard for one filter row in the watched `filters` array.
 */
const isWatchedFilter = (value: unknown): value is WatchedFilter =>
    isRecord(value) && typeof value.field === 'string' && 'value' in value;

/**
 * Normalizes different filter value shapes into a string code.
 * Supports:
 * - raw string codes
 * - ListOfValues-like objects ({ code: string })
 */
export const readCode = (value: unknown): string | null => {
    if (value == null) {
        return null;
    }
    if (typeof value === 'string') {
        return value;
    }
    if (isListOfValuesLike(value)) {
        return value.code;
    }
    return null;
};

/**
 * Finds the value of a filter by field path in the watched filters array.
 * Returns null when the filter is not present or watches are unavailable.
 */
export const getFilterValueByField = (watchedFilters: unknown, field: string): unknown | null => {
    if (!Array.isArray(watchedFilters)) {
        return null;
    }
    const entry = watchedFilters.find((filter) => isWatchedFilter(filter) && filter.field === field);
    return isWatchedFilter(entry) ? entry.value : null;
};

/**
 * Convenience helper for dependent async autocompletes.
 * In our forms, dependency watches are passed as `values`, where index 0 is usually the full `filters` array.
 */
export const getDependentValue = (values: unknown[] | undefined, fieldPath: string): unknown | null => {
    if (!values) {
        return null;
    }
    return getFilterValueByField(values[0], fieldPath);
};

/**
 * Shorthand for "get dependency and extract its code".
 */
export const getDependentCode = (values: unknown[] | undefined, fieldPath: string): string | null =>
    readCode(getDependentValue(values, fieldPath));

/**
 * Creates an equality clause only when a dependency is selected.
 * Keeps callers clean and avoids repetitive null checks.
 */
export const optionalEqualClause = (selector: string, value: string | null | undefined): SpringFilterClause | null =>
    value ? sfEqual(selector, value) : null;

/**
 * Joins optional clauses with logical AND.
 * - no clauses => empty filter
 * - one clause => single clause string
 * - many clauses => sfAnd(...)
 */
export const andClauses = (...clauses: Array<SpringFilterClause | null | undefined>): string => {
    const filteredClauses = clauses.filter((clause): clause is SpringFilterClause => !!clause);
    if (filteredClauses.length === 0) {
        return '';
    }
    if (filteredClauses.length === 1) {
        return filteredClauses[0].toString();
    }
    return sfAnd(filteredClauses).toString();
};
